/**
 * Alerts (WALLIYO-0131): one way for any screen to say something went well, or
 * did not.
 *
 * Before this, every screen kept its own `notice` string and rendered its own
 * banner, which meant a dozen copies of the same markup and a dozen chances
 * for one of them to be a little different. This is the one place.
 *
 * Two kinds, and they behave differently on purpose:
 *
 * - `ok` is something that happened. It takes itself away, because a
 *   confirmation nobody dismissed is a confirmation nobody read, and it
 *   announces politely (`role="status"`) so a screen reader is not interrupted
 *   mid-sentence.
 * - `problem` is something that did not happen. It stays until it is
 *   dismissed, because the person has to decide what to do about it, and it
 *   announces assertively (`role="alert"`).
 *
 * A failure carries its request ID, so somebody can quote it (ADR-0016 §2.5)
 * without going to the browser console to find it.
 */

import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";

import { describeError, type ErrorContext } from "../auth/error-message";
import { t } from "../i18n";

export type AlertKind = "ok" | "problem";

export type Alert = {
  id: number;
  kind: AlertKind;
  message: string;
  /** The request ID line, on a failure that has one. */
  reference: string | null;
};

/** How long a confirmation stays before taking itself away. */
export const OK_ALERT_MS = 6000;

export type Alerts = {
  /** Something happened. Takes itself away. */
  ok: (message: string) => void;
  /**
   * Something did not happen. Stays until dismissed, and reads the error the
   * way every other failure on the console reads it.
   */
  problem: (error: unknown, context?: ErrorContext) => void;
  /** A refusal the screen worked out itself, with no error to read. */
  refuse: (message: string) => void;
  dismiss: (id: number) => void;
  alerts: readonly Alert[];
};

const AlertsContext = createContext<Alerts | null>(null);

export function AlertsProvider({ children }: { children: ReactNode }) {
  const [alerts, setAlerts] = useState<readonly Alert[]>([]);
  const next = useRef(1);
  // Cleared on dismiss as well as on fire, so a confirmation dismissed by hand
  // does not leave a timer that removes whatever took its place.
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: number) => {
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
    setAlerts((current) => current.filter((each) => each.id !== id));
  }, []);

  const add = useCallback(
    (alert: Omit<Alert, "id">) => {
      const id = next.current++;
      setAlerts((current) => [...current, { ...alert, id }]);
      if (alert.kind === "ok") {
        timers.current.set(
          id,
          setTimeout(() => dismiss(id), OK_ALERT_MS),
        );
      }
    },
    [dismiss],
  );

  const value = useMemo<Alerts>(
    () => ({
      alerts,
      dismiss,
      ok: (message) => add({ kind: "ok", message, reference: null }),
      refuse: (message) => add({ kind: "problem", message, reference: null }),
      problem: (error, context) => {
        const { message, reference } = describeError(error, context);
        add({ kind: "problem", message, reference });
      },
    }),
    [alerts, add, dismiss],
  );

  return <AlertsContext.Provider value={value}>{children}</AlertsContext.Provider>;
}

export function useAlerts(): Alerts {
  const alerts = useContext(AlertsContext);
  if (!alerts) throw new Error("useAlerts outside an AlertsProvider");
  return alerts;
}

/**
 * The stack itself, mounted once by the shell.
 *
 * Newest last, so a second failure appears under the first rather than moving
 * it — a message that jumps as you read it is a message you read twice.
 */
export function AlertStack() {
  const { alerts, dismiss } = useAlerts();
  if (alerts.length === 0) return null;
  return (
    <div className="alert-stack" aria-live="polite">
      {alerts.map((alert) => (
        <div
          key={alert.id}
          className={alert.kind === "ok" ? "alert ok" : "alert problem"}
          role={alert.kind === "ok" ? "status" : "alert"}
        >
          <div className="alert-body">
            <span>{alert.message}</span>
            {alert.reference ? <span className="ref">{alert.reference}</span> : null}
          </div>
          <button
            className="alert-close"
            type="button"
            onClick={() => dismiss(alert.id)}
            aria-label={t("alert.dismiss")}
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}
